import { useTraitConnections } from "./TraitConnectionsContext";
import { useMemo, useState } from "react";

export function TraitConnectionsViewer() {
    const [hoveredTrait, setHoveredTrait] = useState<string | null>(null);
    const {
        nodes,
        arcs,
        traitColors,
        nodeSize,
        nodeColor,
        arcStrokeWidth,
        showNodeLabels,
        showArcLabels,
        arcLabelFontSize,
        arcLabelUseArcColor,
        bezierAngle,
        viewBoxAttr,
    } = useTraitConnections();

    const getBezierPath = (
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        angleOffset: number = 0
    ): string => {
        const dx = x2 - x1;
        const dy = y2 - y1;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const lineAngle = Math.atan2(dy, dx);
        
        const angleRad = ((bezierAngle + angleOffset) * Math.PI) / 180;
        const controlDistance = distance * 0.4;
        
        const cp1Angle = lineAngle + angleRad;
        const cp2Angle = lineAngle - angleRad + Math.PI;
        
        const cp1x = x1 + Math.cos(cp1Angle) * controlDistance;
        const cp1y = y1 + Math.sin(cp1Angle) * controlDistance;
        const cp2x = x2 + Math.cos(cp2Angle) * controlDistance;
        const cp2y = y2 + Math.sin(cp2Angle) * controlDistance;
        
        return `M ${x1} ${y1} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x2} ${y2}`;
    };

    // Get point on bezier curve at parameter t (0-1)
    const getBezierPoint = (
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        t: number,
        angleOffset: number = 0
    ): { x: number; y: number } => {
        const dx = x2 - x1;
        const dy = y2 - y1;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const lineAngle = Math.atan2(dy, dx);
        
        const angleRad = ((bezierAngle + angleOffset) * Math.PI) / 180;
        const controlDistance = distance * 0.4;
        
        const cp1Angle = lineAngle + angleRad;
        const cp1x = x1 + Math.cos(cp1Angle) * controlDistance;
        const cp1y = y1 + Math.sin(cp1Angle) * controlDistance;
        const cp2Angle = lineAngle - angleRad + Math.PI;
        const cp2x = x2 + Math.cos(cp2Angle) * controlDistance;
        const cp2y = y2 + Math.sin(cp2Angle) * controlDistance;
        
        const mt = 1 - t;
        const x = mt * mt * mt * x1 + 3 * mt * mt * t * cp1x + 3 * mt * t * t * cp2x + t * t * t * x2;
        const y = mt * mt * mt * y1 + 3 * mt * mt * t * cp1y + 3 * mt * t * t * cp2y + t * t * t * y2;
        
        return { x, y };
    };

    // Estimate text bounding box (approximate)
    const getTextBounds = (text: string, fontSize: number, x: number, y: number) => {
        // Rough estimate: each character is about 0.6 * fontSize wide
        const width = text.length * fontSize * 0.6;
        const height = fontSize;
        return {
            left: x - width / 2,
            right: x + width / 2,
            top: y - height / 2,
            bottom: y + height / 2,
        };
    };

    // Check if two bounding boxes overlap
    const boxesOverlap = (
        box1: { left: number; right: number; top: number; bottom: number },
        box2: { left: number; right: number; top: number; bottom: number }
    ): boolean => {
        return !(
            box1.right < box2.left ||
            box1.left > box2.right ||
            box1.bottom < box2.top ||
            box1.top > box2.bottom
        );
    };

    // Calculate optimal label positions to avoid overlaps
    const labelPositions = useMemo(() => {
        if (!showArcLabels || arcs.length === 0) return new Map<string, number>();

        const positions = new Map<string, number>();
        const maxIterations = 15;
        const tStep = 0.05; // How much to move along the curve
        const tRange = [0.25, 0.75]; // Keep labels in middle portion of arc

        // Initialize all labels at midpoint
        arcs.forEach((arc, idx) => {
            const key = `${arc.from}-${arc.to}-${arc.trait}-${idx}`;
            positions.set(key, 0.5);
        });

        // Get node label bounds if they're shown
        const nodeLabelBounds = showNodeLabels
            ? nodes.map((node) => {
                  const labelY = node.y + nodeSize + 15;
                  // Estimate node label bounds (font size 14, bold)
                  const labelWidth = node.name.length * 14 * 0.6;
                  const labelHeight = 14;
                  return {
                      left: node.x - labelWidth / 2,
                      right: node.x + labelWidth / 2,
                      top: labelY - labelHeight / 2,
                      bottom: labelY + labelHeight / 2,
                      nodeId: node.id,
                  };
              })
            : [];

        // Iteratively resolve overlaps
        for (let iter = 0; iter < maxIterations; iter++) {
            let hasOverlap = false;

            for (let i = 0; i < arcs.length; i++) {
                const arc1 = arcs[i];
                const fromNode1 = nodes.find((n) => n.id === arc1.from);
                const toNode1 = nodes.find((n) => n.id === arc1.to);
                if (!fromNode1 || !toNode1) continue;

                const key1 = `${arc1.from}-${arc1.to}-${arc1.trait}-${i}`;
                const t1 = positions.get(key1) || 0.5;
                const angleOffset1 = arc1.angleOffset || 0;
                const point1 = getBezierPoint(
                    fromNode1.x,
                    fromNode1.y,
                    toNode1.x,
                    toNode1.y,
                    t1,
                    angleOffset1
                );
                const bounds1 = getTextBounds(arc1.trait, arcLabelFontSize, point1.x, point1.y);

                // Check overlap with other arc labels
                for (let j = i + 1; j < arcs.length; j++) {
                    const arc2 = arcs[j];
                    const fromNode2 = nodes.find((n) => n.id === arc2.from);
                    const toNode2 = nodes.find((n) => n.id === arc2.to);
                    if (!fromNode2 || !toNode2) continue;

                    const key2 = `${arc2.from}-${arc2.to}-${arc2.trait}-${j}`;
                    const t2 = positions.get(key2) || 0.5;
                    const angleOffset2 = arc2.angleOffset || 0;
                    const point2 = getBezierPoint(
                        fromNode2.x,
                        fromNode2.y,
                        toNode2.x,
                        toNode2.y,
                        t2,
                        angleOffset2
                    );
                    const bounds2 = getTextBounds(arc2.trait, arcLabelFontSize, point2.x, point2.y);

                    if (boxesOverlap(bounds1, bounds2)) {
                        hasOverlap = true;
                        // Try to adjust positions
                        const currentT1 = positions.get(key1) || 0.5;
                        const currentT2 = positions.get(key2) || 0.5;

                        // Try moving one label
                        let newT1 = currentT1;
                        let newT2 = currentT2;
                        let found = false;

                        // Try moving label1
                        for (let offset = -tStep * 2; offset <= tStep * 2 && !found; offset += tStep) {
                            const testT = Math.max(tRange[0], Math.min(tRange[1], currentT1 + offset));
                            const testPoint = getBezierPoint(
                                fromNode1.x,
                                fromNode1.y,
                                toNode1.x,
                                toNode1.y,
                                testT,
                                angleOffset1
                            );
                            const testBounds = getTextBounds(arc1.trait, arcLabelFontSize, testPoint.x, testPoint.y);
                            if (!boxesOverlap(testBounds, bounds2)) {
                                newT1 = testT;
                                found = true;
                            }
                        }

                        // If that didn't work, try moving label2
                        if (!found) {
                            for (let offset = -tStep * 2; offset <= tStep * 2 && !found; offset += tStep) {
                                const testT = Math.max(tRange[0], Math.min(tRange[1], currentT2 + offset));
                                const testPoint = getBezierPoint(
                                    fromNode2.x,
                                    fromNode2.y,
                                    toNode2.x,
                                    toNode2.y,
                                    testT,
                                    angleOffset2
                                );
                                const testBounds = getTextBounds(arc2.trait, arcLabelFontSize, testPoint.x, testPoint.y);
                                if (!boxesOverlap(bounds1, testBounds)) {
                                    newT2 = testT;
                                    found = true;
                                }
                            }
                        }

                        positions.set(key1, newT1);
                        positions.set(key2, newT2);
                    }
                }

                // Check overlap with node labels
                if (showNodeLabels) {
                    for (const nodeBounds of nodeLabelBounds) {
                        if (boxesOverlap(bounds1, nodeBounds)) {
                            hasOverlap = true;
                            const currentT = positions.get(key1) || 0.5;
                            let newT = currentT;
                            let found = false;

                            // Try moving arc label away from node label
                            // Try both directions
                            for (let offset = -tStep * 3; offset <= tStep * 3 && !found; offset += tStep) {
                                if (offset === 0) continue; // Skip current position
                                const testT = Math.max(tRange[0], Math.min(tRange[1], currentT + offset));
                                const testPoint = getBezierPoint(
                                    fromNode1.x,
                                    fromNode1.y,
                                    toNode1.x,
                                    toNode1.y,
                                    testT,
                                    angleOffset1
                                );
                                const testBounds = getTextBounds(arc1.trait, arcLabelFontSize, testPoint.x, testPoint.y);
                                if (!boxesOverlap(testBounds, nodeBounds)) {
                                    newT = testT;
                                    found = true;
                                }
                            }

                            positions.set(key1, newT);
                        }
                    }
                }
            }

            if (!hasOverlap) break;
        }

        return positions;
    }, [arcs, nodes, showArcLabels, showNodeLabels, arcLabelFontSize, bezierAngle, nodeSize]);

    return (
        <div className="panel-viewer trait-connections-viewer">
            <div className="path-tool-preview">
                <div className="svg-scroll-wrapper">
                    <svg
                        viewBox={viewBoxAttr}
                        xmlns="http://www.w3.org/2000/svg"
                        className="path-tool-svg"
                    >
                        <g className="arcs">
                            {arcs.map((arc, idx) => {
                                const fromNode = nodes.find(
                                    (n) => n.id === arc.from
                                );
                                const toNode = nodes.find(
                                    (n) => n.id === arc.to
                                );
                                if (!fromNode || !toNode) return null;

                                const angleOffset = arc.angleOffset || 0;
                                const pathData = getBezierPath(
                                    fromNode.x,
                                    fromNode.y,
                                    toNode.x,
                                    toNode.y,
                                    angleOffset
                                );
                                const color = traitColors[arc.trait] || "#000000";
                                
                                const key = `${arc.from}-${arc.to}-${arc.trait}-${idx}`;
                                const t = labelPositions.get(key) || 0.5;
                                const labelPoint = getBezierPoint(
                                    fromNode.x,
                                    fromNode.y,
                                    toNode.x,
                                    toNode.y,
                                    t,
                                    angleOffset
                                );

                                const isHovered = hoveredTrait === null || arc.trait === hoveredTrait;
                                const opacity = isHovered ? 1 : 0.2;

                                return (
                                    <g key={key}>
                                        <path
                                            d={pathData}
                                            stroke={color}
                                            strokeWidth={arcStrokeWidth}
                                            fill="none"
                                            style={{ mixBlendMode: "multiply", opacity }}
                                            data-trait={arc.trait}
                                        />
                                        {showArcLabels && (
                                            <text
                                                x={labelPoint.x}
                                                y={labelPoint.y}
                                                fill={arcLabelUseArcColor ? color : "#000000"}
                                                fontSize={arcLabelFontSize}
                                                fontFamily="Arial"
                                                textAnchor="middle"
                                                dominantBaseline="middle"
                                                style={{ opacity }}
                                                data-trait={arc.trait}
                                            >
                                                {arc.trait}
                                            </text>
                                        )}
                                    </g>
                                );
                            })}
                        </g>

                        <g className="nodes">
                            {nodes.map((node) => (
                                <g key={node.id}>
                                    <circle
                                        cx={node.x}
                                        cy={node.y}
                                        r={nodeSize}
                                        fill={nodeColor}
                                        stroke="#333333"
                                        strokeWidth="1"
                                    />
                                    {showNodeLabels && (
                                        <text
                                            x={node.x}
                                            y={node.y + nodeSize + 15}
                                            fill="#333333"
                                            fontSize="14"
                                            fontFamily="Arial"
                                            fontWeight="bold"
                                            textAnchor="middle"
                                            dominantBaseline="middle"
                                        >
                                            {node.name}
                                        </text>
                                    )}
                                </g>
                            ))}
                        </g>

                        {/* Legend */}
                        {Object.keys(traitColors).length > 0 && (() => {
                            const uniqueTraits = Object.entries(traitColors).sort((a, b) => 
                                a[0].localeCompare(b[0])
                            );
                            const legendHeight = uniqueTraits.length * 20 + 10;
                            const legendY = 1000 - legendHeight;
                            const legendX = 1020; // Position legend to the right of the main content
                            return (
                                <g className="legend">
                                    <rect
                                        x={legendX}
                                        y={legendY}
                                        width="200"
                                        height={legendHeight}
                                        fill="white"
                                        stroke="#333333"
                                        strokeWidth="1"
                                        opacity="0.9"
                                    />
                                    {uniqueTraits.map(([trait, color], idx) => (
                                        <g 
                                            key={trait}
                                            className="legend-item"
                                            data-trait={trait}
                                            onMouseEnter={() => setHoveredTrait(trait)}
                                            onMouseLeave={() => setHoveredTrait(null)}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <rect
                                                x={legendX + 10}
                                                y={legendY + 10 + idx * 20}
                                                width="15"
                                                height="15"
                                                fill={color}
                                                stroke="#333333"
                                                strokeWidth="1"
                                            />
                                            <text
                                                x={legendX + 30}
                                                y={legendY + 10 + idx * 20 + 7.5}
                                                fill="#333333"
                                                fontSize="12"
                                                fontFamily="Arial"
                                                dominantBaseline="middle"
                                            >
                                                {trait}
                                            </text>
                                        </g>
                                    ))}
                                </g>
                            );
                        })()}
                    </svg>
                </div>
            </div>
        </div>
    );
}
